import {
  fetchVpcPeersByStackId,
  getStackType,
  hasDeployStack,
  selectIsAccountOwner,
  selectStackById,
  selectVpcPeersByStackId,
} from "@app/deploy";
import { createSupportTicket, resetSupportTicket } from "@app/deploy/support";
import { selectOrganizationSelected } from "@app/organizations";
import { useDispatch, useLoader, useQuery, useSelector } from "@app/react";
import { stackDetailVpcPeeringCreateUrl } from "@app/routes";
import { capitalize } from "@app/string-utils";
import type { DeployVpcPeer } from "@app/types";
import { selectCurrentUser } from "@app/users";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { usePaginate } from "../hooks";
import {
  Banner,
  Box,
  Button,
  DescBar,
  EmptyTr,
  FilterBar,
  Group,
  IconAlertTriangle,
  IconLock,
  IconPlusCircle,
  Input,
  PaginateBar,
  Pill,
  TBody,
  THead,
  Table,
  Td,
  Th,
  Tooltip,
  Tr,
  tokens,
} from "../shared";

const VPCPeerStatusPill = ({
  vpcPeer,
}: {
  vpcPeer: DeployVpcPeer;
}) => {
  const variant = vpcPeer.connectionStatus === "active" ? "success" : "error";
  return <Pill variant={variant}>{capitalize(vpcPeer.connectionStatus)}</Pill>;
};

const VpcPeerDeleteButton = ({
  vpcPeer,
  onDelete,
  isAccountOwner,
  isLoading,
}: {
  vpcPeer: DeployVpcPeer;
  onDelete: (vpcPeer: DeployVpcPeer) => void;
  isAccountOwner: boolean;
  isLoading: boolean;
}) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const isValid = confirmText === vpcPeer.connectionId;

  if (!isAccountOwner) {
    return (
      <Tooltip text="You must be an Account Owner to request VPC peering deletion">
        <Button
          variant="delete"
          size="sm"
          disabled
          className="w-fit opacity-50 cursor-not-allowed"
        >
          <IconLock className="w-4 h-4" />
        </Button>
      </Tooltip>
    );
  }

  const handleDelete = () => {
    if (isValid) {
      onDelete(vpcPeer);
      setShowConfirm(false);
      setConfirmText("");
    }
  };

  if (showConfirm) {
    return (
      <div className="flex flex-col gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
        <p className="text-sm text-red-800">
          Type <strong>{vpcPeer.connectionId}</strong> to confirm deletion
          request:
        </p>
        <p className="text-xs text-red-600">
          This will submit a request to Aptible support to delete this VPC
          peering connection.
        </p>
        <div className="flex gap-2">
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.currentTarget.value)}
            placeholder={vpcPeer.connectionId}
            className="flex-1 text-sm"
          />
          <Button
            variant="delete"
            size="sm"
            onClick={handleDelete}
            disabled={!isValid || isLoading}
            isLoading={isLoading}
          >
            Request Deletion
          </Button>
          <Button
            variant="white"
            size="sm"
            onClick={() => {
              setShowConfirm(false);
              setConfirmText("");
            }}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Button
      variant="delete"
      size="sm"
      onClick={() => setShowConfirm(true)}
      className="w-fit"
      disabled={isLoading}
      isLoading={isLoading}
    >
      Request Deletion
    </Button>
  );
};

// (Wizard removed; create flow now handled by a dedicated page)

export const StackDetailVpcPeeringPage = () => {
  const { id = "" } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const org = useSelector(selectOrganizationSelected);
  const user = useSelector(selectCurrentUser);
  const stack = useSelector((s) => selectStackById(s, { id }));
  const supportTicketLoader = useLoader(createSupportTicket);
  const [deleteRequestSubmitted, setDeleteRequestSubmitted] = useState(false);
  const [createRequestSubmitted, setCreateRequestSubmitted] = useState(false);

  useQuery(fetchVpcPeersByStackId({ id }));

  // Detect create success from navigation state (passed by create page)
  useEffect(() => {
    const state = location.state as { createSuccess?: boolean } | null;
    if (state?.createSuccess) {
      setCreateRequestSubmitted(true);
      // Clear the state to prevent showing banner on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  // Reset loader state on unmount to prevent stale success states
  useEffect(() => {
    return () => {
      dispatch(resetSupportTicket());
    };
  }, [dispatch]);

  // Reset delete request state when delete is successful
  useEffect(() => {
    if (supportTicketLoader.isSuccess && deleteRequestSubmitted) {
      const timer = setTimeout(() => {
        setDeleteRequestSubmitted(false);
      }, 5000); // Hide banner after 5 seconds
      return () => clearTimeout(timer);
    }
  }, [supportTicketLoader.isSuccess, deleteRequestSubmitted]);

  useEffect(() => {
    if (createRequestSubmitted) {
      const timer = setTimeout(() => setCreateRequestSubmitted(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [createRequestSubmitted]);

  const vpcPeers = useSelector((s) =>
    selectVpcPeersByStackId(s, { stackId: id }),
  );
  const paginated = usePaginate(vpcPeers);

  // Check if user is account owner for delete operations
  const isAccountOwner = useSelector((s) =>
    selectIsAccountOwner(s, { orgId: org.id }),
  );

  if (!hasDeployStack(stack)) {
    return null;
  }

  if (getStackType(stack) === "shared") {
    return (
      <Box>
        <h1 className="text-lg text-gray-500 font-semibold flex items-center gap-2 mb-4">
          <IconAlertTriangle color="#6B7280" />
          VPC Peering
        </h1>
        <Banner variant="info">
          VPC Peering is not available on Shared Stacks. Upgrade to a Dedicated
          Stack to peer with your AWS VPC or another Aptible stack.
        </Banner>
      </Box>
    );
  }

  const handleDeleteVpcPeer = (vpcPeer: DeployVpcPeer) => {
    setDeleteRequestSubmitted(true);

    const subject = `VPC Peering Deletion Request - ${stack.name}`;
    const description = `
<p><strong>We've received your request for VPC Peering deletion.</strong> An Aptible Support team member will follow up shortly. If you did not make this request or if you'd like to cancel it, reply here.</p>

<p><strong>Details:</strong></p>
<ul>
  <li><strong>User:</strong> ${user.name} (${user.email})</li>
  <li><strong>Organization:</strong> ${org.name} (ID: ${org.id})</li>
  <li><strong>Stack:</strong> ${stack.name} (ID: ${stack.id})</li>
  <li><strong>VPC Peering Connection ID to Delete:</strong> ${vpcPeer.connectionId}</li>
  <li><strong>VPC Peer Description:</strong> ${vpcPeer.description}</li>
  <li><strong>Current Status:</strong> ${vpcPeer.connectionStatus}</li>
</ul>
    `.trim();

    dispatch(
      createSupportTicket({
        email: user.email,
        name: user.name,
        subject,
        description,
        priority: "normal",
        attachments: [],
      }),
    );
  };

  return (
    <Group>
      <Group size="sm">
        {/* Create VPC Peering Request CTA */}
        <div className="flex items-start mb-3">
          {isAccountOwner ? (
            <Button
              onClick={() => navigate(stackDetailVpcPeeringCreateUrl(id))}
            >
              <IconPlusCircle variant="sm" className="mr-1" /> Create VPC
              Peering Request
            </Button>
          ) : (
            <Tooltip text="You must be an Account Owner to request VPC peering">
              <Button disabled className="opacity-50 cursor-not-allowed">
                <IconPlusCircle variant="sm" className="mr-1" /> Create VPC
                Peering Request
              </Button>
            </Tooltip>
          )}
        </div>

        {createRequestSubmitted && (
          <Banner variant="success" className="mb-4">
            VPC Peering request submitted successfully! The Aptible Team will
            process your request.
          </Banner>
        )}

        <FilterBar>
          <Group variant="horizontal" size="lg" className="items-center">
            <DescBar>{paginated.totalItems} Peers</DescBar>
            <PaginateBar {...paginated} />
          </Group>
        </FilterBar>
      </Group>

      {/* Show delete success message above table */}
      {supportTicketLoader.isSuccess && deleteRequestSubmitted && (
        <Banner variant="success" className="mb-4">
          VPC Peering deletion request submitted successfully! The Aptible Team
          will process your request.
        </Banner>
      )}

      <Table>
        <THead>
          <Th>Status</Th>
          <Th>VPC Peer</Th>
          <Th>Description</Th>
          <Th variant="right">Actions</Th>
        </THead>

        <TBody>
          {paginated.data.length === 0 ? <EmptyTr colSpan={4} /> : null}
          {paginated.data.map((vpcPeer) => (
            <Tr key={vpcPeer.id}>
              <Td>
                <VPCPeerStatusPill vpcPeer={vpcPeer} />
              </Td>
              <Td>
                <span className={tokens.type.darker}>
                  {vpcPeer.connectionId}
                </span>
              </Td>
              <Td>
                <span className={tokens.type.darker}>
                  {vpcPeer.description}
                </span>
              </Td>
              <Td variant="right">
                <VpcPeerDeleteButton
                  vpcPeer={vpcPeer}
                  onDelete={handleDeleteVpcPeer}
                  isAccountOwner={isAccountOwner}
                  isLoading={supportTicketLoader.isLoading}
                />
              </Td>
            </Tr>
          ))}
        </TBody>
      </Table>
      {paginated.totalPages > 1 && <PaginateBar {...paginated} />}
    </Group>
  );
};
